import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';
import { PLATFORM_HEADERS } from './platform';

// ponytail: base URL is a stub until the real Public/Internal API host is confirmed.
const BASE = import.meta.env.VITE_125_PLATFORM_PUBLIC_BASE_URL;

export interface GetCompaniesParams {
	page?: number;
	pageSize?: number;
	/** Return every match in one response instead of a page. Overrides page/pageSize. */
	all?: boolean;
	/** Case-insensitive name filter, applied in the query so the totals describe it. */
	search?: string;
	includeInactive?: boolean;
}

const response = {
	// GET /companies — internal, cross-tenant, offset pagination.
	// Params are omitted rather than sent falsey: the endpoint's defaults (active only,
	// page 1, 50 per page) are the ones we want, and `params` is fed straight to
	// URLSearchParams, which would stringify an absent value as "undefined".
	async getCompanies({
		page,
		pageSize,
		all,
		search,
		includeInactive,
	}: GetCompaniesParams = {}): Promise<any> {
		const term = search?.trim();
		const query: Record<string, string> = {};

		if (all) {
			query.all = 'true';
		} else {
			query.page = String(page ?? 1);
			query.page_size = String(pageSize ?? 50);
		}

		if (term) {
			query.search = term;
		}

		if (includeInactive) {
			query.include_inactive = 'true';
		}

		return await api(
			HTTP_TYPES.GET,
			`${BASE}/companies`,
			undefined,
			query,
			'application/json',
			'v1',
			{ ...PLATFORM_HEADERS },
		);
	},
	// GET /companies/sites — scoped to the company via the x-company-id header.
	// Always asks for every status: the endpoint defaults to active only, and each
	// consumer decides for itself what to show. `exclude_from_reporting` and
	// `show_in_guest_surveys` are deliberately not sent — every field they filter on
	// comes back on the row, so the filtering happens in the browser.
	async getSites(companyId: string | number): Promise<any> {
		return await api(
			HTTP_TYPES.GET,
			`${BASE}/companies/sites`,
			undefined,
			{ include_inactive: 'true' },
			'application/json',
			'v1',
			{ ...PLATFORM_HEADERS, 'x-company-id': String(companyId) },
		);
	},
};

export default response;
